import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { StoredCustomerLicenses, UserLicense } from "../../Utils/types";

export interface User {
  id: number | null;
  firstName: string;
  lastName: string;
  custID: number | null;
  email: string;
  ringTimeout: number;
  isAnonymous: boolean;
  disableOutbound: boolean;
  ringTimeoutSecs: number;
  fromCGS: boolean;
  usergrpID: number | null;
  primaryExtenID: number | null;
  primaryDIDNumber: string | null;
  pager: string;
  callerName: string;
  callerNumber: string;
  i911CallerNumber: string;
  callForwardNumber: string;
  callForwardBusyNumber: string;
  callForwardNoAnswerNumber: string;
  includeInDirectory: boolean;
  rejectAnonymous: boolean;
  allowBLF: boolean;
  callForwardUnavailNumber: string;
  disableInternational: boolean;
  loginName: string;
  accountID: number | null;
  remoteCallFwdPin: string;
  preferredCodec: string;
  disable411: boolean;
  restrictedPatterns: string;
  callForwardEnableHard: boolean;
  loginType: string;
  enableCallerIDPassThru: boolean;
  inboundPresentationRules: string;
  disableN11: boolean;
  disableOperatorAssist: boolean;
  callForwardUseInternalCallerID: boolean;
  callForwardRingInternalLines: boolean;
  disableFaxReinvites: boolean;
  authPin: string;
  callerIDPassThruMode: boolean;
  disableDomestic: boolean;
  disableMobile: boolean;
  disablePremium: boolean;
  disableInternationalUK: boolean;
  emergencyCallerIDPassThruEnable: boolean;
  callRecord: boolean;
  recordCallMessageRecordingID: number | null;
  i911CallerNumberModifyDate: string;
  userEnabledInvitation: boolean;
  userEnabledDownloadICS: boolean;
  userDefaultCalendarView: string;
  userAutoMail: boolean;
  inactiveSessionTimeout: number | undefined;
}

export interface SuperAdminUser {
  id: number | null;
  email: string;
  firstName: string;
  lastName: string;
  state: string;
  groupId: number;
  groupAdmin: boolean;
  loginType: string;
  custID: number | null; //if is customer selected
}

interface UserState {
  token: string | null;
  user: User | SuperAdminUser | null;
  ldap: boolean;
  meetingAnalyticsPermission: boolean;
  licenseTypes: StoredCustomerLicenses[];
}

const initialState: UserState = {
  token: null,
  user: null,
  ldap: false,
  meetingAnalyticsPermission: false,
  licenseTypes: [],
};

export const userChange = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserState>) => {
      state.ldap = action.payload.ldap;
      state.token = action.payload.token;
      state.user = JSON.parse(JSON.stringify(action.payload.user));
      state.meetingAnalyticsPermission =
        action.payload.meetingAnalyticsPermission;
    },
    updateFormValues: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload,
        };
      }
    },

    clearUser: (state, action) => {
      state.ldap = initialState.ldap;
      state.token = initialState.token;
      state.user = initialState.user;
    },

    changeCustId: (state, action: PayloadAction<number>) => {
      if (state?.user) state.user.custID = action.payload;
    },

    setLicenseTypes: (
      state,
      action: PayloadAction<StoredCustomerLicenses[]>
    ) => {
      state.licenseTypes = action.payload;
    },
    addToLicenseTypes: (state, action: PayloadAction<UserLicense>) => {
      state.licenseTypes = state.licenseTypes.map((license) => {
        if (license.key === action.payload.customerLicenseId) {
          license.userLicenses.push(action.payload);
        }
        return license;
      });
    },
    deleteFromLicenseTypes: (
      state,
      action: PayloadAction<{ userLicenseId: number }>
    ) => {
      state.licenseTypes = state.licenseTypes.map((license) => {
        license.userLicenses = license.userLicenses.filter(
          (userLicense) => userLicense.id !== action.payload.userLicenseId
        );
        return license;
      });
    },
  },
});

export const {
  setUser,
  clearUser,
  updateFormValues,
  changeCustId,
  setLicenseTypes,
  addToLicenseTypes,
  deleteFromLicenseTypes,
} = userChange.actions;

export default userChange.reducer;
